import { useCallback, useEffect, useState } from "react";

import { useGetRolesQuery } from "@js/apps/roles/api";
import { useAppDispatch, useAppSelector } from "@js/hooks";
import { useEffectRef } from "@js/hooks/use-effect-ref";
import type { FreelancerRole } from "@js/types/freelancer";

import { fetchFreelancerProfile } from "../../actions";
import { freelancerApi, useGetFreelancerRolesQuery } from "../../api";
import type { RolesFormData } from "../../forms/role-form";

type RoleModalState =
  | {
      kind: null;
    }
  | {
      kind: "list";
    }
  | {
      kind: "add";
      initialValues?: RolesFormData;
    }
  | {
      kind: "edit";
      initialValues: RolesFormData;
    }
  | {
      kind: "remove";
      role: FreelancerRole;
    };

export const useRoleModal = ({
  onFinished,
  initialValues,
}: {
  onFinished?: () => void;
  initialValues?: RolesFormData;
}) => {
  const { data: _roles, isLoading: isRolesLoading } = useGetRolesQuery(); // prefetch
  const { data: freelancerRoles = [], isLoading: isFreelancerRolesLoading } =
    useGetFreelancerRolesQuery();
  const freelancerProfile = useAppSelector(
    (state) => state.freelancer.freelancerProfile,
  );
  const dispatch = useAppDispatch();
  const [modal, setModal] = useState<RoleModalState>({ kind: null });
  const remainingRoleCount =
    SETTINGS.FREELANCER_ROLES_MAX - freelancerRoles.length;
  const maxRoleCountReached = remainingRoleCount === 0;
  const canSaveAndAddAnother = remainingRoleCount >= 2;

  const initialValuesRef = useEffectRef(initialValues);
  useEffect(() => {
    if (isFreelancerRolesLoading) {
      return;
    }

    setModal((current) => {
      const isInitialKind = !current.kind;
      const isListKind = current.kind === "list";
      if ((isInitialKind || isListKind) && !freelancerRoles?.length) {
        return {
          kind: "add",
          initialValues: { primary: true, ...initialValuesRef.current },
        };
      }

      if (isInitialKind) {
        return { kind: "list" };
      }

      return current;
    });
  }, [freelancerRoles, isFreelancerRolesLoading, initialValuesRef]);

  useEffect(() => {
    if (modal.kind === "add" && maxRoleCountReached) {
      setModal({ kind: "list" });
    }
  }, [modal.kind, maxRoleCountReached, setModal]);

  const onRoleChange = useCallback(() => {
    if (!freelancerProfile?.id) return null;

    void dispatch(fetchFreelancerProfile(freelancerProfile?.id));
    dispatch(freelancerApi.util.invalidateTags(["FreelancerPublicProfile"]));
  }, [freelancerProfile?.id, dispatch]);

  const handleAdd = useCallback(() => {
    setModal({ kind: "add" });
  }, []);

  const handleEdit = useCallback((role: RolesFormData) => {
    setModal({ kind: "list" });
    setTimeout(() => {
      setModal({ kind: "edit", initialValues: role });
    });
  }, []);

  const handleDelete = useCallback((role: FreelancerRole) => {
    setModal({ kind: "remove", role });
  }, []);

  const handleSaveRole = useCallback(
    (addAnother?: boolean) => {
      if (addAnother) {
        setModal({ kind: "add" });
      } else {
        setModal({ kind: "list" });
        onFinished?.();
      }
      onRoleChange();
    },
    [onFinished, onRoleChange],
  );

  const handleCancel = useCallback(() => {
    if (freelancerRoles.length) {
      setModal({ kind: "list" });
    }
  }, [freelancerRoles.length]);

  const isLoading = isRolesLoading || isFreelancerRolesLoading;

  return {
    roles: freelancerRoles,
    modal,
    canSaveAndAddAnother,
    maxRoleCountReached,
    handleAdd,
    handleEdit,
    handleDelete,
    handleSaveRole,
    handleCancel,
    isLoading,
  };
};
